// Per-route search metadata for easypeazy.site, shared by the React app
// (RouteMeta, ServicePage) and the post-build prerender script
// (scripts/prerender-seo.mjs). Pure functions with no imports, so Node can load
// this file directly; callers pass in the parsed src/seo/pages.json.
//
// Every claim in pages.json must be true of the live product today. Prices come
// from backend/server.py (WEBSITE_ANNUAL_USD = 12) and PricingPage.js. Do not
// add ratings, reviews, addresses or phone numbers to the structured data.

export function normalizePath(path) {
  const clean = String(path || '/').split(/[?#]/)[0].replace(/\/+$/, '');
  return clean || '/';
}

export function findPage(data, path) {
  const want = normalizePath(path);
  return data.pages.find((p) => p.path === want) || null;
}

export function canonicalFor(data, page) {
  return data.site.origin + (page.path === '/' ? '/' : page.path);
}

export function organizationId(data) {
  return `${data.site.origin}/#organization`;
}

// Site-wide structured data. Lives in public/index.html too; kept here so the
// prerender and the app agree on the @id.
export function organizationJsonLd(data) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': organizationId(data),
        name: data.site.name,
        url: `${data.site.origin}/`,
        logo: `${data.site.origin}/apple-touch-icon.png`,
      },
      {
        '@type': 'WebSite',
        '@id': `${data.site.origin}/#website`,
        name: data.site.name,
        url: `${data.site.origin}/`,
        publisher: { '@id': organizationId(data) },
      },
    ],
  };
}

// Route-specific structured data: a Service per service page (with an Offer
// only where the code states the price) and an FAQPage only when the page
// shows those questions and answers.
export function routeJsonLd(data, page) {
  const url = canonicalFor(data, page);
  const graph = [];
  if (page.kind === 'service' && page.service) {
    const s = page.service;
    const service = {
      '@type': 'Service',
      '@id': `${url}#service`,
      name: s.name,
      serviceType: s.serviceType,
      description: page.description,
      url,
      provider: { '@id': organizationId(data) },
    };
    if (s.audience) service.audience = { '@type': 'Audience', audienceType: s.audience };
    if (s.offer) {
      service.offers = {
        '@type': 'Offer',
        price: s.offer.price,
        priceCurrency: s.offer.priceCurrency,
        description: s.offer.description,
        url: `${data.site.origin}/pricing`,
      };
    }
    graph.push(service);
  }
  if (page.faqs && page.faqs.length) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: page.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }
  if (!graph.length) return null;
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function servicePages(data) {
  return data.services.map((path) => findPage(data, path)).filter(Boolean);
}

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// JSON inside <script type="application/ld+json">: escape "<" so no value can
// close the script element.
export function jsonForScript(value) {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

// Plain HTML for the page body before the app starts. Crawlers that do not run
// JavaScript read this; the React app replaces it on load with the same words.
export function staticBodyHtml(data, page) {
  const e = escapeHtml;
  const a = 'style="color:#a5b4fc"';
  const out = [];
  out.push('<div style="max-width:48rem;margin:0 auto;padding:6rem 1.5rem 3rem;color:#e2e8f0;line-height:1.6">');
  out.push(`<p><a href="/" ${a}>${e(data.site.name)}</a> · <a href="/pricing" ${a}>Pricing</a></p>`);
  out.push('<main>');
  out.push(`<h1>${e(page.h1)}</h1>`);
  if (page.lede) out.push(`<p>${e(page.lede)}</p>`);
  for (const section of page.sections || []) {
    out.push(`<h2>${e(section.heading)}</h2>`);
    for (const para of section.paragraphs || []) out.push(`<p>${e(para)}</p>`);
    if (section.list) out.push(`<ul>${section.list.map((li) => `<li>${e(li)}</li>`).join('')}</ul>`);
    if (section.steps) out.push(`<ol>${section.steps.map((li) => `<li>${e(li)}</li>`).join('')}</ol>`);
    if (section.link) out.push(`<p><a href="${e(section.link.href)}" ${a}>${e(section.link.label)}</a></p>`);
  }
  if (page.faqs && page.faqs.length) {
    out.push('<h2>Questions</h2>');
    for (const f of page.faqs) out.push(`<h3>${e(f.q)}</h3><p>${e(f.a)}</p>`);
  }
  if (page.cta) out.push(`<p><a href="${e(page.cta.to)}" ${a}>${e(page.cta.label)}</a></p>`);
  const related = (page.related || []).map((p) => findPage(data, p)).filter(Boolean);
  if (related.length) {
    out.push('<h2>Related</h2>');
    out.push(`<ul>${related.map((r) => `<li><a href="${e(r.path)}" ${a}>${e(r.navLabel || r.h1)}</a></li>`).join('')}</ul>`);
  }
  out.push('</main>');
  out.push('<nav aria-label="Services"><p>What we offer:</p><ul>');
  for (const s of servicePages(data)) out.push(`<li><a href="${e(s.path)}" ${a}>${e(s.navLabel)}</a></li>`);
  out.push('</ul></nav>');
  out.push(`<p><a href="/terms" ${a}>Terms</a> · <a href="/privacy" ${a}>Privacy</a></p>`);
  if (data.site.parent) out.push(`<p>EasyPeazy is part of <a href="${e(data.site.parent.url)}" ${a}>${e(data.site.parent.name)}</a>.</p>`);
  out.push('</div>');
  return out.join('\n');
}
